import "react-phone-input-2/lib/style.css";

import { useFormik } from "formik";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdAddCircleOutline } from "react-icons/md";
import PhoneInput from "react-phone-input-2";

import { ReactComponent as EyeIcon } from "../../../assets/eye-closed.svg";
import { ReactComponent as EyeSlashIcon } from "../../../assets/eye-open.svg";
import { useTheme } from "../../../context/ThemeContext";
import useAuth from "../../../hooks/useAuth";
import { useSnack } from "../../../hooks/useSnack";
import schoolServices from "../../../services/schoolServices";
import { getLanguage } from "../../../utils/getLanguage";
import { addTeacherModalValidationSchema, validatePhoneNumber } from "../../../utils/validationSchema";
import Button from "../../Button";
import LoadingBtn from "../../LoadingBtn";
import Modal from "../../Modal";

const TextInput = ({ name, label, type = "text", placeholder, helpText, formik }) => (
  <div className="relative">
    <div className="mb-1 flex items-end space-x-1">
      <label className="block text-sm font-medium text-mutedText dark:text-dark-mutedText">{label}</label>
      {helpText && (
        <div className="group relative mt-[-5px]">
          <span className="cursor-pointer rounded-full bg-black px-[4px] text-[10px] text-white dark:text-gray-500">
            ?
          </span>
          <div className="absolute left-0 top-5 z-10 hidden w-44 rounded-md bg-gray-800 p-2 text-xs text-white shadow-md group-hover:block">
            <p className="text-left">{helpText}</p>
          </div>
        </div>
      )}
      {formik.touched[name] && (formik.errors[name] === "Required" || formik.errors[name] === "مطلوب") && (
        <span className="text-start text-xl leading-3 text-red-300">*</span>
      )}
    </div>
    <input
      name={name}
      type={type}
      placeholder={placeholder}
      className="block w-full rounded-md border bg-[#F9FAFB] px-3 py-2 text-sm placeholder:text-mutedText focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-10 dark:border-dark-background dark:bg-dark-background dark:text-white"
      value={formik.values[name]}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
    />
    {formik.touched[name] && formik.errors[name] !== "Required" && formik.errors[name] !== "مطلوب" && (
      <p className="mt-1 text-start text-xs text-red-300">{formik.errors[name]}</p>
    )}
  </div>
);

const PasswordInput = ({ name, label, placeholder, formik, isRtl, visibility, toggleVisibility }) => (
  <div>
    <div className="mb-1 flex items-end space-x-1">
      <label className="block text-sm font-medium text-mutedText dark:text-dark-mutedText">{label}</label>
      {formik.touched[name] && (formik.errors[name] === "Required" || formik.errors[name] === "مطلوب") && (
        <span className="text-start text-xl leading-3 text-red-300">*</span>
      )}
    </div>
    <div className="relative">
      <input
        name={name}
        type={visibility ? "text" : "password"}
        placeholder={placeholder}
        className="dark:bg-dark-backgrounddark:text-dark-mutedText block w-full rounded-md border bg-[#F9FAFB] px-3 py-2 text-sm placeholder:text-mutedText focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-10 dark:border-dark-background dark:text-white"
        value={formik.values[name]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        autoComplete="new-password"
      />
      <span
        className={`absolute ${isRtl ? "left-2" : "right-2"} top-1/2 -translate-y-1/2 cursor-pointer`}
        onClick={toggleVisibility}
      >
        {visibility ? (
          <EyeSlashIcon className="h-4 w-4 text-mutedText dark:text-dark-mutedText" />
        ) : (
          <EyeIcon className="h-4 w-4 text-mutedText dark:text-dark-mutedText" />
        )}
      </span>
    </div>
    {formik.touched[name] && formik.errors[name] !== "Required" && formik.errors[name] !== "مطلوب" && (
      <p className="mt-1 text-start text-xs text-red-300">{formik.errors[name]}</p>
    )}
  </div>
);

const AddTeacherModal = forwardRef(({ onSubmit }, ref) => {
  const user = useAuth();
  const isThirdPartyUser = user?.is_b12;
  const lang = getLanguage();
  const isRtl = lang === "ar";
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { showSnack } = useSnack();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [phoneCountryCode, setPhoneCountryCode] = useState("JO"); // Default country: Jordan

  useImperativeHandle(ref, () => ({
    openModal() {
      setIsOpen(true);
    },
    closeModal() {
      setIsOpen(false);
    },
  }));

  const togglePasswordVisibility = () => setPasswordVisible(!passwordVisible);
  const toggleConfirmPasswordVisibility = () => setConfirmPasswordVisible(!confirmPasswordVisible);

  const formik = useFormik({
    initialValues: {
      name: "",
      username: "",
      email: "",
      b12_email: "",
      phone: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: addTeacherModalValidationSchema(t, isThirdPartyUser),
    validate: (values) => {
      const errors = {};
      const phoneError = validatePhoneNumber(values.phone, phoneCountryCode, t);
      if (phoneError) errors.phone = phoneError;
      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      try {
        const payload = new FormData();
        payload.append("name", values.name);
        if (!isThirdPartyUser) {
          payload.append("username", values.username);
          payload.append("email", values.email);
          payload.append("password", values.password);
          payload.append("password_confirmation", values.confirmPassword);
        } else {
          payload.append("b12_email", values.b12_email);
        }
        payload.append("phone_number ", values.phone);

        const response = await schoolServices.addNewTeacher(payload);
        showSnack(response.message, "success");
        onSubmit(values);
        resetForm();
        setIsOpen(false);
        setTimeout(() => {
          setIsSubmitting(false);
        }, 500);
      } catch (error) {
        showSnack(error, "error");
        setIsSubmitting(false);
      }
    },
  });

  const handleCloseModal = () => {
    formik.resetForm(); // Reset the form
    setIsOpen(false); // Close the modal
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      header={
        <div className="flex items-center justify-between">
          <p className="flex gap-2 text-green-500">
            <MdAddCircleOutline size={22} />
            {t("add_new_teacher")}
          </p>
        </div>
      }
      footer={
        <div className="flex items-center justify-end space-x-2">
          <Button label={t("cancel")} onClick={handleCloseModal} color="white" variant="outlined" />
          <Button
            className="w-36"
            color="green"
            disabled={isSubmitting}
            loading={isSubmitting}
            onClick={formik.handleSubmit}
            label={t("done")}
          />
        </div>
      }
    >
      <form>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-3 gap-y-2">
            <TextInput
              name="name"
              label={t("teacher_name")}
              placeholder={t("teacher_name")}
              formik={formik}
            />
            {!isThirdPartyUser && (
              <TextInput
                name="username"
                label={t("username")}
                placeholder={t("username")}
                helpText={t("firstname_Lastname_SchoolName")}
                formik={formik}
              />
            )}

            {!isThirdPartyUser ? (
              <TextInput name="email" label={t("email")} placeholder="example@example.com" formik={formik} />
            ) : (
              <TextInput
                name="b12_email"
                label={t("b12_email")}
                placeholder="example@b12.com"
                formik={formik}
              />
            )}
            <div>
              <div className="mb-1 flex items-end space-x-1">
                <label className="block text-sm font-medium text-mutedText dark:text-dark-mutedText">
                  {t("phone")}
                </label>
                {formik.touched.phone &&
                  (formik.errors.phone === "Required" || formik.errors.phone === "مطلوب") && (
                    <span className="text-start text-xl leading-3 text-red-300">*</span>
                  )}
              </div>
              <PhoneInput
                country="jo"
                placeholder={t("enter_phone_number")}
                excludeCountries={["il"]}
                onChange={(phone, { countryCode }) => {
                  formik.setFieldValue("phone", phone);
                  setPhoneCountryCode(countryCode.toUpperCase());
                }}
                value={formik.values.phone}
                inputStyle={{
                  height: 37,
                  color: "#adadad",
                  width: "100%",
                  borderColor: theme === "dark" ? "#080d17" : "#E5E7EB",
                  backgroundColor: theme === "dark" ? "#080d17" : "#F9FAFB",
                  borderRadius: 7,
                }}
              />
              {formik.touched.phone &&
                formik.errors.phone !== "Required" &&
                formik.errors.phone !== "مطلوب" && (
                  <p className="mt-1 text-start text-xs text-red-300">{formik.errors.phone}</p>
                )}
            </div>
            {!isThirdPartyUser && (
              <>
                <PasswordInput
                  name="password"
                  label={t("password")}
                  placeholder={t("password")}
                  formik={formik}
                  isRtl={isRtl}
                  visibility={passwordVisible}
                  toggleVisibility={togglePasswordVisibility}
                />
                <PasswordInput
                  name="confirmPassword"
                  label={t("confirm_password")}
                  placeholder={t("confirm_password")}
                  formik={formik}
                  isRtl={isRtl}
                  visibility={confirmPasswordVisible}
                  toggleVisibility={toggleConfirmPasswordVisibility}
                />
              </>
            )}
          </div>
        </div>
      </form>
    </Modal>
  );
});

export default AddTeacherModal;
